import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/axios';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const TypebotBuilder = () => {
  const { typebotId } = useParams();
  const navigate = useNavigate();
  const [builderUrl, setBuilderUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    const fetchBuilderUrl = async () => {
      try {
        const response = await api.get(`/typebot/builder-url/${typebotId}`);

        // The backend sets the __Secure-next-auth.session-token cookie via
        // Set-Cookie header (HttpOnly, domain=.chatza.co.za) so the builder
        // iframe on builder.chatza.co.za is pre-authenticated.
        setBuilderUrl(response.data.builderUrl);
      } catch (err) {
        console.error('Failed to get builder URL:', err);
        setError(err.response?.data?.message || 'Failed to load builder');
      } finally {
        setLoading(false);
      }
    };

    fetchBuilderUrl();
  }, [typebotId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="text-center">
          <div className="spinner spinner-lg mb-4" />
          <p className="text-neutral-500">Loading Chatbot Builder...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-800 mb-2">Failed to Load Builder</h2>
          <p className="text-neutral-500 mb-6">{error}</p>
          <button onClick={() => navigate('/typebot')} className="btn-primary">
            Back to Chatbots
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Floating back button */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={() => navigate('/typebot')}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-lg shadow-md hover:bg-neutral-50 transition-all text-sm font-medium text-neutral-700"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Chatbots
        </button>
      </div>

      {/* Loading overlay for iframe */}
      {!iframeLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-5">
          <div className="text-center">
            <div className="spinner spinner-lg mb-4" />
            <p className="text-neutral-500">Connecting to builder...</p>
          </div>
        </div>
      )}

      {/* Builder iframe */}
      <iframe
        src={builderUrl}
        title="Chatbot Builder"
        className="w-full h-full border-0"
        onLoad={() => setIframeLoaded(true)}
        allow="clipboard-read; clipboard-write"
      />
    </div>
  );
};

export default TypebotBuilder;
